import React, { useEffect, useState, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { OfflineProvider, useOffline } from './contexts/OfflineContext';
import PrivateRoute from './components/PrivateRoute';
import { setupAxiosInterceptors } from './services/api';
import ToastContainer, { showToast } from './components/Toast';
import OfflineIndicator from './components/common/OfflineIndicator';
import { offlineHelpers } from './services/db';

// Initialize i18n for multi-language support
import './i18n/config';

// Lazy load pages for code splitting - reduces initial bundle size
const Login = lazy(() => import('./pages/Login'));
const TechnicianDashboard = lazy(() => import('./pages/TechnicianDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));

/**
 * Loading spinner for lazy-loaded components
 */
const LoadingSpinner: React.FC = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: 'linear-gradient(135deg, #122b4a 0%, #0c1f38 100%)',
  }}>
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
    }}>
      <div style={{
        width: '48px',
        height: '48px',
        border: '4px solid rgba(201, 162, 39, 0.25)',
        borderTopColor: '#c9a227',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
      <span style={{
        color: '#f6f4ef',
        fontSize: '14px',
        fontWeight: 500,
      }}>Loading...</span>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  </div>
);

/**
 * Main application wrapper with offline indicator
 */
const AppContent: React.FC = () => {
  const { isOnline, pendingRequests } = useOffline();

  const handleRetrySync = useCallback(async () => {
    try {
      await offlineHelpers.syncQueue();
      showToast('Data synced successfully!', 'success');
    } catch (error) {
      showToast('Sync failed. Please try again.', 'error');
      throw error;
    }
  }, []);

  // Show offline indicator when offline or has pending items
  const showIndicator = !isOnline || pendingRequests.length > 0;

  return (
    <div className="app">
      <ToastContainer />
      {showIndicator && (
        <OfflineIndicator
          isOnline={isOnline}
          pendingCount={pendingRequests.length}
          onRetrySync={handleRetrySync}
        />
      )}
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/technician/*"
            element={
              <PrivateRoute role="TECHNICIAN">
                <TechnicianDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/*"
            element={
              <PrivateRoute role="ADMIN">
                <AdminDashboard />
              </PrivateRoute>
            }
          />
          <Route path="/" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
};

/**
 * Main App component
 */
function App() {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Setup axios interceptors
    setupAxiosInterceptors();
    setIsInitialized(true);

    // Log app version in console for debugging.
    // v1.4.19 (2026-10-15): PM-10b — selective acceptance (office accept/return),
    // completion evidence (photo/measurement/checkbox), withdraw; SW v30.
    // v1.4.18 (2026-10-15): PM-10a — Stop working vs Finish activity, exact SOW
    // instance on every start, Continue your work, Hide with reasons + resurfacing,
    // office Attention tab; SW v29.
    // v1.4.17 (2026-10-11): PM-9c — per-job chart readability (label column, drafts
    // as markers, same-day targets as milestones, weekly axis, hour sources, tooltips); SW v28.
    // v1.4.16 (2026-10-11): PM-9b — Cancel item on cost-bearing checklist rows,
    // cancel reason required; backend auto-done for free-list completions; SW v27.
    // v1.4.15 (2026-10-11): SOW round 1 / PM-9 — stable Projects page (no self-
    // refresh), estimated hours per catalog activity, KPI order-confirmation
    // drives target dates + auto-confirms SOW, Approve-all SOW, PO pending/Set PO;
    // admin-side only, technician payloads unchanged; SW v26.
    // v1.4.14 (2026-09-07): PM-8 — auto-done linkage + audited revert,
    // technicians queue dashboard (drag reorder/reassign), overview filters +
    // per-job charts (admin-side only; technician payloads unchanged); SW v25.
    // v1.4.13 (2026-09-07): PM-7 — workspace overview dashboard, SOW templates,
    // bulk assignment (admin-side only); SW v24.
    // v1.4.12 (2026-09-06): PRIV-1 — client identity removed from ALL technician
    // surfaces (queue card client_name; materials price_per_unit); SW v23.
    // v1.4.11 (2026-09-05): PM-6 field-feedback round 2 (SOW-before-work gate,
    // PO-once + estimated hours, completed-or-paused, multi-assign, green-lock,
    // enforced prerequisites + per-job editor, portrait fix, item-activity
    // association, SOW-scoped picker); SW cache v21. Use this banner to confirm a device has picked up the new
    // bundle after close-and-reopen.
    console.log(
      '%c Arabian Barq Operations v1.4.19 ',
      'background: #122b4a; color: #c9a227; font-size: 14px; padding: 4px 8px; border-radius: 4px;'
    );
  }, []);

  if (!isInitialized) {
    return null;
  }

  return (
    <Router>
      <AuthProvider>
        <OfflineProvider>
          <AppContent />
        </OfflineProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
